import { ReactNode, useEffect, useMemo, useRef } from 'react'
import {
  useFloating,
  autoUpdate,
  useInteractions,
  useDismiss,
  useRole,
  FloatingPortal,
  Placement
} from '@floating-ui/react'
import { offset, shift, flip } from '@floating-ui/dom'
import { Box, useTheme } from '@mui/material'
import React from 'react'
import { FloatingArrow, arrow } from '@floating-ui/react'
import Logger from 'services/Logger'
import i18n from 'i18n'

export type PopupSurfaceLevel = 'primary' | 'secondary'

interface TFloatingPopoverProps {
  content: ReactNode
  anchorElement: HTMLElement | null
  open: boolean
  onClose: () => void
  id?: string
  closeOnTouchOutside?: boolean
  onOutsideClick?: () => void
  defaultPlacement?: 'left' | 'right' | 'top' | 'bottom'
  fallbackPlacements?: Placement[]
  rightClickClose?: boolean
  padding?: number
  borderRadius?: number | string
  popupBorder?: string
  surfaceLevel?: PopupSurfaceLevel
  closeOnEscape?: boolean
}

const TFloatingPopover = (props: TFloatingPopoverProps) => {
  const theme = useTheme()
  const {
    open,
    onClose,
    anchorElement,
    content,
    closeOnTouchOutside: closeOnTouchOutside = false,
    onOutsideClick,
    closeOnEscape: closeOnEscape = false
  } = props

  const arrowRef = useRef(null)
  const lastPositionRef = useRef<{ x: number; y: number } | null>(null)

  const { x, y, strategy, refs, context, placement } = useFloating({
    open,
    onOpenChange: onClose,
    elements: { reference: anchorElement },
    placement:
      props.defaultPlacement ?? (i18n.dir() === 'ltr' ? 'right' : 'left'),
    middleware: [
      offset({ mainAxis: 10, crossAxis: 0 }),
      flip({
        fallbackStrategy: 'initialPlacement',
        padding: 8,
        fallbackPlacements: props.fallbackPlacements ?? [
          props.defaultPlacement ?? (i18n.dir() === 'ltr' ? 'right' : 'left'),
          i18n.dir() === 'ltr' ? 'right' : 'left',
          'top'
        ]
      }),
      shift({ padding: 8 }),
      arrow({
        element: arrowRef
      })
    ],
    whileElementsMounted: autoUpdate
  })

  // Preserve last position while open
  useEffect(() => {
    if (
      open &&
      anchorElement &&
      typeof x === 'number' &&
      typeof y === 'number'
    ) {
      lastPositionRef.current = { x, y }
    }
    if (!open) {
      lastPositionRef.current = null
    }
  }, [open, anchorElement, x, y])

  const { getFloatingProps } = useInteractions([
    useDismiss(context, {
      outsidePress: (event) => {
        return closeOnTouchOutside
      },
      escapeKey: closeOnEscape ?? false
    }),
    useRole(context, { role: 'tooltip' })
  ])

  const floatingProps = getFloatingProps({
    onClick: (event) => {
      event.stopPropagation()
    },
    onMouseDown: (event) => {
      event.stopPropagation()
    }
  })

  const isCutOff = x < 0

  const isAnchorValid = anchorElement && document.body.contains(anchorElement)
  const isDark = theme.palette.mode === 'dark'
  const isPrimarySurface = props.surfaceLevel !== 'secondary'
  const popupBorderColor = 'rgba(255, 255, 255, 0.3)'
  const popupBorder = props.popupBorder ?? `1px solid ${popupBorderColor}`
  const popupShadow = isDark
    ? isPrimarySurface
      ? '0px 8px 24px rgba(0, 0, 0, 0.28), 0px 14px 34px rgba(0, 0, 0, 0.34)'
      : '0px 10px 26px rgba(0, 0, 0, 0.34), 0px 18px 42px rgba(0, 0, 0, 0.42)'
    : '0px 4px 20px rgba(0, 0, 0, 0.15), 0px 8px 40px rgba(0, 0, 0, 0.3)'

  const popoverStyle = useMemo(() => {
    const isTopPlacement = placement.startsWith('top')

    // Use last known position if anchor is not valid
    if (!isAnchorValid && lastPositionRef.current) {
      return {
        top: lastPositionRef.current.y,
        left: lastPositionRef.current.x,
        position: 'fixed',
        borderRadius: props.borderRadius ?? '16px',
        boxShadow: popupShadow,
        padding: theme.spacing(props.padding ?? 2, 0),
        backgroundColor: theme.palette.card.primary,
        zIndex: 1300
      }
    }

    if (isCutOff) {
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'fixed',
        borderRadius: props.borderRadius ?? '16px',
        boxShadow: popupShadow,
        padding: theme.spacing(props.padding ?? 2, 0),
        backgroundColor: theme.palette.card.primary,
        zIndex: 1300
      }
    }

    return {
      top: y ?? 0,
      left: x ?? 0,
      position: strategy,
      borderRadius: props.borderRadius ?? '16px',
      boxShadow: popupShadow,
      padding: theme.spacing(2, 0),
      backgroundColor: theme.palette.card.primary,
      zIndex: 1300,
      transform: isTopPlacement ? 'translateY(-5px)' : 'translateY(5px)'
    }
  }, [
    x,
    y,
    strategy,
    placement,
    anchorElement,
    isCutOff,
    isAnchorValid,
    props.borderRadius,
    props.padding,
    props.surfaceLevel,
    popupShadow,
    theme
  ])

  if (!open) return null

  return (
    <FloatingPortal>
      <div
        style={{
          display: closeOnTouchOutside || onOutsideClick ? 'flex' : 'none',
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.0)',
          zIndex: closeOnTouchOutside ? 1299 : onOutsideClick ? 999 : 1
        }}
        onContextMenu={(e) => {
          e.preventDefault()
          e.stopPropagation()

          if (props.rightClickClose && onOutsideClick) {
            onOutsideClick()
          }
        }}
        onClick={
          closeOnTouchOutside ? (onOutsideClick ?? onClose) : onOutsideClick
        }
      />
      <Box
        ref={refs.setFloating}
        sx={{
          ...(popoverStyle as any),
          border: popupBorder,
          padding: theme.spacing(props.padding ?? 2, 0)
        }}
        {...floatingProps}
      >
        {content}

        {!isCutOff && isAnchorValid && (
          <FloatingArrow
            width={20}
            height={12}
            stroke={popupBorderColor}
            strokeWidth={1}
            style={{ marginTop: '-4px' }}
            fill={theme.palette.card.primary}
            ref={arrowRef}
            context={context}
          />
        )}
      </Box>
    </FloatingPortal>
  )
}

export default React.memo(TFloatingPopover)
